import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { setReduxSignRequest } from "redux/appSlice";
import { logOutAction } from "redux/authSlice";
import { useAppDispatch } from "redux/hooks";
import { AppModal, bodyTextSx } from "global/components/AppModal";

const InActiveUserModel = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const events = ["mousemove", "keydown", "scroll", "touchstart"];

  const resetInactivityTimeout = () => {
    if (inactivityTimeoutRef.current)
      clearTimeout(inactivityTimeoutRef.current);
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);

    inactivityTimeoutRef.current = setTimeout(() => {
      setIsModalOpen(true);
      popupTimeoutRef.current = setTimeout(() => {
        dispatch(setReduxSignRequest(undefined));
        dispatch(logOutAction(null));
      }, 1 * 60 * 1000); // 1 minute popup interaction
    }, 15 * 60 * 1000); // 15 minutes of inactivity
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setCountdown(60);
    resetInactivityTimeout();
  };

  useEffect(() => {
    const handleUserActivity = () => {
      if (!isModalOpen) resetInactivityTimeout();
    };

    events.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );
    // Initial inactivity timeout setup
    resetInactivityTimeout();
    // Clean up event listeners on component unmount
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
      if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    };
  }, [isModalOpen]);

  useEffect(() => {
    // Countdown for the popup
    let interval: number | null = null;
    if (isModalOpen) {
      interval = window.setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            if (interval) clearInterval(interval); // Clear the countdown interval
            setIsModalOpen(false);
            setTimeout(() => {
              dispatch(setReduxSignRequest(undefined));
              dispatch(logOutAction(null));
            }, 0);
            return 60;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval !== null) clearInterval(interval);
    };
  }, [isModalOpen]);

  const handleLogOut = async () => {
    await dispatch(setReduxSignRequest(undefined));
    await dispatch(logOutAction(null));
  };

  return (
    <AppModal
      open={isModalOpen}
      onClose={handleClose}
      // The countdown is the headline; the paragraph below it explains why the
      // session is about to end.
      title={`You will be logged out in ${countdown} seconds.`}
      tone="neutral"
      size="sm"
      ariaLabel="Session timeout warning"
      // Dismissing by scrim or Esc would silently restart the timer, so the
      // choice has to be made explicitly.
      disableBackdropClose
      primaryLabel="Keep Working"
      onPrimary={handleClose}
      cancelLabel="Log Out"
      onCancel={handleLogOut}
    >
      <Typography sx={bodyTextSx}>
        You have been inactive for a while. Please confirm that you are still
        here to stay logged in.
      </Typography>
    </AppModal>
  );
};

export default InActiveUserModel;
